'use client';

/* eslint-disable react/no-context-provider */

import type { PwaInstallContextValue } from '@/components/features/pwa/pwaInstallContext';
import type { MarketLocale } from '@/libs/market/market';
import { useQueryClient } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import dynamic from 'next/dynamic';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PwaInstallContext } from '@/components/features/pwa/pwaInstallContext';
import { useLockBodyScroll } from '@/hooks/useLockBodyScroll';
import { usePathname } from '@/i18n/routing';
import { getManifestPathForLocale } from '@/libs/pwa/manifest';
import { QueriesKeys } from '@/queries/queriesKeys';
import { userApi } from '@/queries/user/user.api';
import { useAppSelector } from '@/redux/hooks';

const PwaInstallWebComponentDynamic = dynamic(
  () => import('./PwaInstallWebComponent').then(mod => ({ default: mod.PwaInstallWebComponent })),
  { ssr: false },
);

type InstallPromptOutcome = 'accepted' | 'dismissed';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: InstallPromptOutcome; platform: string }>;
};

function isAppleDevice(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const platform = navigator.platform;
  const userAgent = navigator.userAgent;

  return /iPad|iPhone|iPod/.test(platform)
    || /Mac/.test(platform)
    || (/Mac/.test(platform) && navigator.maxTouchPoints > 1)
    || /iPad|iPhone|iPod|Macintosh/.test(userAgent);
}

function isStandaloneMode(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia('(display-mode: standalone)').matches
    || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

function isAndroidDevice(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return /Android/i.test(navigator.userAgent);
}

function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const browserWindow = window as Window & {
    defferedPromptEvent?: BeforeInstallPromptEvent | null;
  };

  return browserWindow.defferedPromptEvent ?? null;
}

function clearDeferredInstallPrompt() {
  const browserWindow = window as Window & {
    defferedPromptEvent?: BeforeInstallPromptEvent | null;
  };

  browserWindow.defferedPromptEvent = null;
}

function isAcceptedInstallChoice(event: Event): boolean {
  return (event as CustomEvent<{ message?: string }>).detail?.message === 'accepted';
}

export function PwaInstallProvider({ children }: { children: React.ReactNode }) {
  const locale = useLocale() as MarketLocale;
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const [mounted, setMounted] = useState(false);
  const userSession = useAppSelector(s => s.user.userSession);
  const loading = useAppSelector(s => s.user.loading);
  const trackedInstallRef = useRef(false);

  const [installElement, setInstallElement] = useState<PWAInstallElement | null>(null);
  const [isInstallAvailable, setIsInstallAvailable] = useState(false);
  const [isStandalone, setIsStandalone] = useState(isStandaloneMode);
  const [isAppleDeviceState, setIsAppleDeviceState] = useState(false);
  const [canDirectInstall, setCanDirectInstall] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const canUseDirectInstallPrompt = useCallback(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return Boolean(getDeferredInstallPrompt());
  }, []);

  const syncInstallState = useCallback(() => {
    const standalone = installElement?.isUnderStandaloneMode || isStandaloneMode();
    const appleDevice = isAppleDevice();
    const installAvailable = Boolean(installElement?.isInstallAvailable) && !standalone;

    setIsStandalone(standalone);
    setIsAppleDeviceState(appleDevice);
    setIsInstallAvailable(installAvailable);
    setCanDirectInstall(installAvailable && !appleDevice && canUseDirectInstallPrompt());
  }, [canUseDirectInstallPrompt, installElement]);

  const trackAppInstall = useCallback(() => {
    if (trackedInstallRef.current || !userSession) {
      return;
    }

    trackedInstallRef.current = true;
    void userApi.trackAppInstall()
      .then(() => Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueriesKeys.user] }),
        queryClient.invalidateQueries({ queryKey: [QueriesKeys.mePlayerQuests] }),
      ]))
      .catch(() => {
        trackedInstallRef.current = false;
      });
  }, [queryClient, userSession]);

  const handleInstallAccepted = useCallback(() => {
    setIsInstallAvailable(false);
    trackAppInstall();
  }, [trackAppInstall]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQueryList = window.matchMedia('(display-mode: standalone)');
    let beforeInstallPromptTimer: number | null = null;

    const handleModeChange = () => {
      syncInstallState();
    };

    const handleAppInstalled = () => {
      handleModeChange();
      trackAppInstall();
    };

    const handleBeforeInstallPrompt = () => {
      beforeInstallPromptTimer = window.setTimeout(() => {
        syncInstallState();
      }, 0);
    };

    syncInstallState();

    mediaQueryList.addEventListener('change', handleModeChange);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      if (beforeInstallPromptTimer) {
        window.clearTimeout(beforeInstallPromptTimer);
      }

      mediaQueryList.removeEventListener('change', handleModeChange);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [syncInstallState, trackAppInstall]);

  useEffect(() => {
    if (!installElement) {
      return;
    }

    const onInstallAvailable: EventListener = () => {
      syncInstallState();
    };

    const onInstallSuccess: EventListener = () => {
      setIsStandalone(true);
      handleInstallAccepted();
    };

    const onUserChoiceResult: EventListener = (event) => {
      if (!isAcceptedInstallChoice(event)) {
        return;
      }

      setIsInstallAvailable(false);
      trackAppInstall();
    };

    installElement.addEventListener('pwa-install-available-event', onInstallAvailable);
    installElement.addEventListener('pwa-install-success-event', onInstallSuccess);
    installElement.addEventListener('pwa-user-choice-result-event', onUserChoiceResult);

    const timer = window.setTimeout(syncInstallState, 0);

    return () => {
      window.clearTimeout(timer);
      installElement.removeEventListener('pwa-install-available-event', onInstallAvailable);
      installElement.removeEventListener('pwa-install-success-event', onInstallSuccess);
      installElement.removeEventListener('pwa-user-choice-result-event', onUserChoiceResult);
    };
  }, [handleInstallAccepted, installElement, syncInstallState, trackAppInstall]);

  useEffect(() => {
    if (!installElement) {
      return;
    }

    let observer: MutationObserver | null = null;
    let cancelled = false;

    const syncDialogOpen = () => {
      setIsDialogOpen(Boolean(installElement.shadowRoot?.querySelector('.install-dialog.available')));
    };

    // The web component attaches its shadow root only after its async connectedCallback
    // resolves, so wait for the first Lit render instead of reading shadowRoot right away.
    void installElement.updateComplete.then(() => {
      const shadowRoot = installElement.shadowRoot;

      if (cancelled || !shadowRoot) {
        return;
      }

      observer = new MutationObserver(syncDialogOpen);

      observer.observe(shadowRoot, {
        attributeFilter: ['class'],
        attributes: true,
        childList: true,
        subtree: true,
      });

      syncDialogOpen();
    });

    return () => {
      cancelled = true;
      observer?.disconnect();
      setIsDialogOpen(false);
    };
  }, [installElement]);

  useEffect(() => {
    if (!installElement || !isDialogOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (event.composedPath().includes(installElement)) {
        return;
      }

      installElement.hideDialog();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [installElement, isDialogOpen]);

  useLockBodyScroll(isDialogOpen);

  const openInstallDialog = useCallback(() => {
    installElement?.showDialog(true);
  }, [installElement]);

  const startInstallFlow = useCallback(async () => {
    const directInstallPrompt = getDeferredInstallPrompt();

    if (directInstallPrompt) {
      installElement?.hideDialog();

      try {
        await directInstallPrompt.prompt();
        const { outcome } = await directInstallPrompt.userChoice;

        if (outcome === 'accepted') {
          handleInstallAccepted();
        }
      } finally {
        clearDeferredInstallPrompt();
      }

      return;
    }

    installElement?.showDialog(true);
  }, [handleInstallAccepted, installElement]);

  const value = useMemo<PwaInstallContextValue>(() => ({
    isInstallAvailable,
    isStandalone,
    isAppleDevice: isAppleDeviceState,
    canDirectInstall,
    shouldShowFloatingCta: isInstallAvailable,
    shouldShowInstallInPushSettings: isAppleDeviceState && !isStandalone,
    startInstallFlow,
    openInstallDialog,
    handleInstallAccepted,
  }), [canDirectInstall, handleInstallAccepted, isAppleDeviceState, isInstallAvailable, isStandalone, openInstallDialog, startInstallFlow]);

  const isInstallLandingPath = pathname === '/install' || pathname.startsWith('/install/');
  const shouldUseNativeAndroidInstallFlow = mounted && isInstallLandingPath && isAndroidDevice();
  const showPwaInstall = mounted && !loading && userSession && !shouldUseNativeAndroidInstallFlow;
  const manifestUrl = getManifestPathForLocale(locale);

  return (
    <PwaInstallContext.Provider value={value}>
      {children}
      {showPwaInstall && (
        <PwaInstallWebComponentDynamic
          installElementRef={setInstallElement}
          manifestUrl={manifestUrl}
        />
      )}
    </PwaInstallContext.Provider>
  );
}

export { usePwaInstall } from './pwaInstallContext';
